import { useState, useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { useReportDeliveryTypes } from "@/hooks/use-delivery-types"
import type { ReportDelivery } from "@/types/delivery"

// Simple email validation regex - allows multiple emails separated by semicolons or commas.
// The TLD group excludes dots ([^\s@.]+$) to prevent quadratic backtracking (ReDoS).
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@.]+$/

function validateEmails(value: string): boolean {
  if (!value.trim()) return true // Empty is valid (optional field)
  const emails = value.split(/[;,]/).map((e) => e.trim()).filter(Boolean)
  return emails.every((email) => email.length <= 254 && EMAIL_REGEX.test(email))
}

interface DeliveryConfigDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  delivery: ReportDelivery | null
  onSave: (delivery: Partial<ReportDelivery>) => void
}

interface DeliveryFormProps {
  delivery: ReportDelivery | null
  onSave: (delivery: Partial<ReportDelivery>) => void
  onCancel: () => void
}

function DeliveryForm({ delivery, onSave, onCancel }: Readonly<DeliveryFormProps>) {
  const { t } = useTranslation()
  const { data: deliveryTypes = [] } = useReportDeliveryTypes()
  const [activeTab, setActiveTab] = useState("general")

  // Form state - initialize from delivery prop (fresh on each mount)
  const [formState, setFormState] = useState({
    reportDeliveryTypeKey: delivery?.reportDeliveryTypeKey || "",
    reportFileTypeKey: delivery?.reportFileTypeKey || "",
    reportCollationTypeKey: delivery?.reportCollationTypeKey || "",
    emailTo: delivery?.emailTo || "",
    emailCC: delivery?.emailCC || "",
    emailFrom: delivery?.emailFrom || "",
    emailReplyTo: delivery?.emailReplyTo || "",
    emailSubject: delivery?.emailSubject || "",
    emailMessage: delivery?.emailMessage || "",
    fileName: delivery?.fileName || "",
    filePath: delivery?.filePath || "",
    printerName: delivery?.printerName || "",
    printerPath: delivery?.printerPath || "",
  })

  const updateField = (field: string, value: string) => {
    setFormState((prev) => ({ ...prev, [field]: value }))
  }

  // Destructure for convenience
  const {
    reportDeliveryTypeKey,
    reportFileTypeKey,
    reportCollationTypeKey,
    emailTo,
    emailCC,
    emailFrom,
    emailReplyTo,
    emailSubject,
    emailMessage,
    fileName,
    filePath,
    printerName,
    printerPath,
  } = formState

  // Validation state
  const emailErrors = useMemo(() => ({
    emailTo: !validateEmails(emailTo),
    emailCC: !validateEmails(emailCC),
    emailFrom: !validateEmails(emailFrom),
    emailReplyTo: !validateEmails(emailReplyTo),
  }), [emailTo, emailCC, emailFrom, emailReplyTo])

  const hasEmailErrors = Object.values(emailErrors).some(Boolean)

  const handleSave = () => {
    if (hasEmailErrors) {
      setActiveTab("email")
      return
    }
    onSave(formState)
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t("dialogs.deliveryConfig.title")}</DialogTitle>
      </DialogHeader>
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="general">{t("dialogs.deliveryConfig.tabs.general")}</TabsTrigger>
          <TabsTrigger value="email">{t("dialogs.deliveryConfig.tabs.email")}</TabsTrigger>
          <TabsTrigger value="file">{t("dialogs.deliveryConfig.tabs.filePrinter")}</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="deliveryType">{t("dialogs.deliveryConfig.fields.deliveryType")}</Label>
            <Select
              value={reportDeliveryTypeKey}
              onValueChange={(v) => updateField("reportDeliveryTypeKey", v)}
            >
              <SelectTrigger id="deliveryType">
                <SelectValue placeholder={t("dialogs.deliveryConfig.placeholders.selectDeliveryType")} />
              </SelectTrigger>
              <SelectContent>
                {deliveryTypes.map((type) => (
                  <SelectItem
                    key={type.reportDeliveryTypeKey}
                    value={type.reportDeliveryTypeKey}
                  >
                    {type.name || type.reportDeliveryTypeKey}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="fileType">{t("dialogs.deliveryConfig.fields.fileType")}</Label>
            <Input
              id="fileType"
              value={reportFileTypeKey}
              onChange={(e) => updateField("reportFileTypeKey", e.target.value)}
              placeholder={t("dialogs.deliveryConfig.placeholders.fileType")}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="collationType">{t("dialogs.deliveryConfig.fields.collationType")}</Label>
            <Input
              id="collationType"
              value={reportCollationTypeKey}
              onChange={(e) => updateField("reportCollationTypeKey", e.target.value)}
              placeholder={t("dialogs.deliveryConfig.placeholders.collationType")}
            />
          </div>
        </TabsContent>

        <TabsContent value="email" className="space-y-4 pt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="emailTo">{t("dialogs.deliveryConfig.fields.emailTo")}</Label>
              <Input
                id="emailTo"
                value={emailTo}
                onChange={(e) => updateField("emailTo", e.target.value)}
                placeholder={t("dialogs.deliveryConfig.placeholders.emailTo")}
                className={emailErrors.emailTo ? "border-destructive" : ""}
              />
              {emailErrors.emailTo && (
                <p className="text-xs text-destructive">{t("dialogs.deliveryConfig.errors.invalidEmail")}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="emailCC">{t("dialogs.deliveryConfig.fields.emailCc")}</Label>
              <Input
                id="emailCC"
                value={emailCC}
                onChange={(e) => updateField("emailCC", e.target.value)}
                placeholder={t("dialogs.deliveryConfig.placeholders.emailCc")}
                className={emailErrors.emailCC ? "border-destructive" : ""}
              />
              {emailErrors.emailCC && (
                <p className="text-xs text-destructive">{t("dialogs.deliveryConfig.errors.invalidEmail")}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="emailFrom">{t("dialogs.deliveryConfig.fields.emailFrom")}</Label>
              <Input
                id="emailFrom"
                value={emailFrom}
                onChange={(e) => updateField("emailFrom", e.target.value)}
                placeholder={t("dialogs.deliveryConfig.placeholders.emailFrom")}
                className={emailErrors.emailFrom ? "border-destructive" : ""}
              />
              {emailErrors.emailFrom && (
                <p className="text-xs text-destructive">{t("dialogs.deliveryConfig.errors.invalidEmail")}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="emailReplyTo">{t("dialogs.deliveryConfig.fields.emailReplyTo")}</Label>
              <Input
                id="emailReplyTo"
                value={emailReplyTo}
                onChange={(e) => updateField("emailReplyTo", e.target.value)}
                placeholder={t("dialogs.deliveryConfig.placeholders.emailReplyTo")}
                className={emailErrors.emailReplyTo ? "border-destructive" : ""}
              />
              {emailErrors.emailReplyTo && (
                <p className="text-xs text-destructive">{t("dialogs.deliveryConfig.errors.invalidEmail")}</p>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="emailSubject">{t("dialogs.deliveryConfig.fields.emailSubject")}</Label>
            <Input
              id="emailSubject"
              value={emailSubject}
              onChange={(e) => updateField("emailSubject", e.target.value)}
              placeholder={t("dialogs.deliveryConfig.placeholders.emailSubject")}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="emailMessage">{t("dialogs.deliveryConfig.fields.emailMessage")}</Label>
            <Textarea
              id="emailMessage"
              value={emailMessage}
              onChange={(e) => updateField("emailMessage", e.target.value)}
              placeholder={t("dialogs.deliveryConfig.placeholders.emailMessage")}
              rows={4}
            />
          </div>
        </TabsContent>

        <TabsContent value="file" className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="fileName">{t("dialogs.deliveryConfig.fields.fileName")}</Label>
            <Input
              id="fileName"
              value={fileName}
              onChange={(e) => updateField("fileName", e.target.value)}
              placeholder={t("dialogs.deliveryConfig.placeholders.fileName")}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="filePath">{t("dialogs.deliveryConfig.fields.filePath")}</Label>
            <Input
              id="filePath"
              value={filePath}
              onChange={(e) => updateField("filePath", e.target.value)}
              placeholder={t("dialogs.deliveryConfig.placeholders.filePath")}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="printerName">{t("dialogs.deliveryConfig.fields.printerName")}</Label>
            <Input
              id="printerName"
              value={printerName}
              onChange={(e) => updateField("printerName", e.target.value)}
              placeholder={t("dialogs.deliveryConfig.placeholders.printerName")}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="printerPath">{t("dialogs.deliveryConfig.fields.printerPath")}</Label>
            <Input
              id="printerPath"
              value={printerPath}
              onChange={(e) => updateField("printerPath", e.target.value)}
              placeholder={t("dialogs.deliveryConfig.placeholders.printerPath")}
            />
          </div>
        </TabsContent>
      </Tabs>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          {t("actions.cancel")}
        </Button>
        <Button onClick={handleSave}>{t("actions.save")}</Button>
      </DialogFooter>
    </>
  )
}

export function DeliveryConfigDialog({
  open,
  onOpenChange,
  delivery,
  onSave,
}: Readonly<DeliveryConfigDialogProps>) {
  const handleSave = (formData: Partial<ReportDelivery>) => {
    onSave(formData)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        {open && (
          <DeliveryForm
            delivery={delivery}
            onSave={handleSave}
            onCancel={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
